import { api, type paths } from './client';
import type { components } from './types.gen';
export type CastingContextInput = components['schemas']['CastingContextInput'];
export type CastingReference = paths['/admin/artists/{artistId}/casting-reference']['get']['responses'][200]['content']['application/json'];
export type CastingContext = NonNullable<paths['/admin/events/{eventId}/casting-context']['get']['responses'][200]['content']['application/json']['context']>;
export type CastingContextSettings = paths['/admin/events/{eventId}/casting-context']['get']['responses'][200]['content']['application/json'];
export async function updateEventCastingContext(eventId: string, body: CastingContextInput): Promise<CastingContextSettings> {
  return requireData(await api.PUT('/admin/events/{eventId}/casting-context', { params: { path: { eventId } }, body }));
}
export const CASTING_RANKS = ['S', 'A', 'B', 'C', 'D', 'E', 'F'] as const;
export const CASTING_STYLES = [
  ['1', 'ポップスライク'], ['2', 'ポップス寄り'], ['3', '中間・クロスオーバー'],
  ['4', 'クラシック寄り'], ['5', 'クラシックライク'], ['mixed', '混在（ひとつに置けない）'],
] as const;

function requireData<T>(result: { data?: T; response: Response; error?: unknown }): T {
  if (!result.response.ok || !result.data) {
    const error = result.error as { message?: string } | undefined;
    throw Object.assign(new Error(error?.message || '参考情報を取得・保存できませんでした。'), { status: result.response.status });
  }
  return result.data;
}
export async function getEventCastingContext(eventId: string, signal?: AbortSignal) {
  return requireData(await api.GET('/admin/events/{eventId}/casting-context', { params: { path: { eventId } }, signal }));
}
export async function getCastingReference(artistId: string, signal?: AbortSignal): Promise<CastingReference> {
  return decodeCastingReference(requireData(await api.GET('/admin/artists/{artistId}/casting-reference', { params: { path: { artistId } }, signal })));
}
export async function setGenreCastingProfile(genreId: number, profileKey: 'orchestra_v1' | null, expectedProfileKey: 'orchestra_v1' | null) {
  return requireData(await api.PUT('/admin/genres/{genreId}/casting-axis-profile', {
    params: { path: { genreId } }, body: { profileKey, expectedProfileKey },
  }));
}

export function decodeCastingReference(raw: unknown): CastingReference {
  const data = raw as CastingReference | null;
  if (!data || data.basis !== 'past_confirmed_contracts' || data.unit !== 'events' || !Array.isArray(data.groups)
    || !data.groups.every(g => g && Number.isSafeInteger(g.genreId) && typeof g.genreLabel === 'string'
      && g.profileKey === 'orchestra_v1' && (!g.distribution || (['all', 'recent'] as const).every(period => {
        const d = g.distribution![period];
        return d && Number.isSafeInteger(d.unpositioned) && d.unpositioned >= 0 && Array.isArray(d.clusters) && d.clusters.length <= 196
          && d.clusters.every(c => c && Number.isSafeInteger(c.count) && c.count > 0 && typeof c.key === 'string'
            && [c.x, c.y, c.minX, c.maxX, c.minY, c.maxY].every(n => Number.isFinite(n) && n >= 0 && n <= 1)
            && Array.isArray(c.tiles) && c.tiles.length > 0 && c.tiles.length <= 196 && c.tiles.every(n => Number.isSafeInteger(n) && n >= 0 && n < 196));
      })) && Array.isArray(g.cells) && g.cells.length <= 56 && g.cells.every(c => c &&
        (c.rank === null || CASTING_RANKS.includes(c.rank as typeof CASTING_RANKS[number]))
        && (c.style === null || CASTING_STYLES.some(([v]) => v === c.style))
        && Number.isSafeInteger(c.count) && c.count >= 0 && Number.isSafeInteger(c.recentCount) && c.recentCount >= 0 && c.recentCount <= c.count))) {
    throw new Error('参考情報の形式を確認できませんでした。再読み込みしてください。');
  }
  return data;
}

export type CastingPosition = NonNullable<CastingContextInput['position']>;
export type CastingReferenceEvents = paths['/admin/artists/{artistId}/casting-reference/events']['get']['responses'][200]['content']['application/json'];
export type CastingEventsQuery = { genreId: number; tiles: string; period: 'all' | 'recent'; offset?: number };
export const castingClusterBand = (count: number) => count >= 10 ? 3 : count >= 5 ? 2 : count >= 2 ? 1 : 0;
export const CASTING_COUNT_BANDS = ['1件', '2〜4件', '5〜9件', '10件以上'];
export async function getCastingReferenceEvents(query: CastingEventsQuery, artistId: string, signal?: AbortSignal): Promise<CastingReferenceEvents> {
  return requireData(await api.GET('/admin/artists/{artistId}/casting-reference/events', { params: { path: { artistId }, query }, signal }));
}

export type CastingPeerEvents = paths['/admin/genres/{genreId}/casting-reference-events']['get']['responses'][200]['content']['application/json'];
export function decodeCastingPeerEvents(raw: unknown): CastingPeerEvents {
  const data = raw as CastingPeerEvents | null;
  if (!data || !Array.isArray(data.events) || data.events.length > 30
    || !data.events.every(e => e && typeof e.eventId === 'string' && typeof e.title === 'string'
      && typeof e.lastDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(e.lastDate)
      && e.position && [e.position.x, e.position.y].every(n => Number.isFinite(n) && n >= 0 && n <= 1)
      && (e.flyerFrontUrl == null || typeof e.flyerFrontUrl === 'string'))) {
    throw new Error('参考公演の形式を確認できませんでした。');
  }
  return data;
}
export async function getRecentCastingEvents(genreId: number, excludeEventId?: string, signal?: AbortSignal): Promise<CastingPeerEvents> {
  return decodeCastingPeerEvents(requireData(await api.GET('/admin/genres/{genreId}/casting-reference-events', {
    params: { path: { genreId }, query: { excludeEventId } }, signal,
  })));
}
